import { Icon, Divider, Loader, Message } from 'semantic-ui-react';
import SusuModal from '../common/SusuModal';
import PersonImageEditor from '../person-image-editor/PersonImageEditor';
import DeletionWarningModal from '../deletion-warning-modal/DeletionWarningModal';
import ProcessingModal from '../common/ProcessingModal';

// Local components
import ClothingActionButtons from './ClothingActionButtons';
import ClothingSelectedControls from './ClothingSelectedControls';
import ClothingDeleteConfirmModal from './ClothingDeleteConfirmModal';
import useClothingModalHandlers from './useClothingModalHandlers';

import './ClothingModal.css';

/**
 * ClothingModal - Modal for managing clothing items
 * 
 * Features:
 * - Upload photos from device or camera
 * - Edit clothing name
 * - Crop/resize images
 * - Remove/toggle background
 * - Delete items with cascade warning for VTO results
 */
const ClothingModal = ({
    isOpen,
    onClose,
    editItemId = null // If provided, opens in edit mode for this item
}) => {
    const {
        // Refs
        fileInputRef,

        // State
        deleteConfirmOpen,
        deleteError,
        bgRemovalError,
        setBgRemovalError,
        editingName,
        tempName,
        setTempName,
        nameError,
        setNameError,
        isEditorOpen,
        setIsEditorOpen,
        editorImageUrl,
        isSavingCrop,
        showDeletionWarning,
        vtoCount,
        deletionError,
        showBgRemovalProcessing,
        existingClothing,
        isCheckingExisting,
        uploadProgress,
        generalError,
        setGeneralError,

        // Computed
        isOperationInProgress,
        isEditMode,
        preview,
        clothesName,
        isPublic,
        setIsPublic,
        hasChanges,

        // Data
        creditBalance,
        hasCredits,
        daysUntilReset,
        hasRemovedBg,
        useRemovedImage,

        // Handlers
        handleFileInputClick,
        handleLocalFileUpload,
        handleRemoveExisting,
        handleRemoveBackgroundClick,
        handleToggleBackground,
        handleConfirmDelete,
        handleCancelDelete,
        handleCancelDeletionWarning,
        handleConfirmCascadeDelete,
        handleSaveName,
        handleStartEditName,
        handleOpenEditor,
        handleEditorSave,
        handleDeleteClick,
        handleSubmit,

        // Multi-upload
        handleMultiFileUpload,
        multiUploadProgress,
        isMultiUploading
    } = useClothingModalHandlers({ onClose, editItemId, isOpen });

    return (
        <>
            <SusuModal
                open={isOpen}
                onClose={onClose}
                className="clothing-modal"
                size="small"
                closeIcon
            >
                {isOperationInProgress && (
                    <div className="modal-loading-overlay">
                        <Loader active size="large" />
                    </div>
                )}

                <SusuModal.Header>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Icon name="shopping bag" style={{ margin: 0 }} />
                        <span>{isEditMode ? 'Edit Item' : 'Upload Clothing'}</span>
                    </div>
                </SusuModal.Header>

                <SusuModal.Content scrolling>
                    <div className="clothing-modal-content">
                        {/* Action Buttons */}
                        <ClothingActionButtons
                            fileInputRef={fileInputRef}
                            onFileInputClick={handleFileInputClick}
                            onLocalFileUpload={handleLocalFileUpload}
                            isOperationInProgress={isOperationInProgress || isMultiUploading}
                            preview={preview}
                            isEditMode={isEditMode}
                        />

                        {/* Error Messages */}
                        {bgRemovalError && (
                            <Message negative onDismiss={() => setBgRemovalError(null)}>
                                <Message.Header>Background Removal Failed</Message.Header>
                                <p>{bgRemovalError}</p>
                            </Message>
                        )}

                        {nameError && (
                            <Message negative onDismiss={() => setNameError(null)}>
                                <Message.Header>Update Name Failed</Message.Header>
                                <p>{nameError}</p>
                            </Message>
                        )}

                        {generalError && (
                            <Message negative onDismiss={() => setGeneralError(null)}>
                                <p>{generalError}</p>
                            </Message>
                        )}

                        {/* Show if checking for existing clothing */}
                        {isCheckingExisting && (
                            <Message info>
                                <Icon name="search" loading />
                                Checking if this item already exists...
                            </Message>
                        )}

                        {/* Show if clothing already exists */}
                        {!isCheckingExisting && existingClothing && (
                            <Message warning>
                                <Message.Header>
                                    <Icon name="warning sign" />
                                    Already in Closet
                                </Message.Header>
                                <p>
                                    This item is already in your closet as <strong>{existingClothing.clothesName || 'Unnamed Item'}</strong>.
                                </p>
                                <button
                                    className="ui red small button"
                                    onClick={handleRemoveExisting}
                                    disabled={isOperationInProgress}
                                >
                                    <Icon name="trash" />
                                    Remove from Closet
                                </button>
                            </Message>
                        )}

                        {preview && (
                            <>
                                <Divider />

                                {/* Selected Image Controls */}
                                <ClothingSelectedControls
                                    preview={preview}
                                    clothesName={clothesName}
                                    editingName={editingName}
                                    tempName={tempName}
                                    setTempName={setTempName}
                                    onSaveName={handleSaveName}
                                    onStartEditName={handleStartEditName}
                                    onOpenEditor={handleOpenEditor}
                                    onRemoveBackgroundClick={handleRemoveBackgroundClick}
                                    onToggleBackground={handleToggleBackground}
                                    onDeleteClick={handleDeleteClick}
                                    onSubmit={handleSubmit}
                                    isOperationInProgress={isOperationInProgress}
                                    hasCredits={hasCredits}
                                    creditBalance={creditBalance}
                                    daysUntilReset={daysUntilReset}
                                    isEditMode={isEditMode}
                                    hasRemovedBg={hasRemovedBg}
                                    useRemovedImage={useRemovedImage}
                                    uploadProgress={uploadProgress}
                                    hasChanges={hasChanges}
                                    isPublic={isPublic}
                                    setIsPublic={setIsPublic}
                                />
                            </>
                        )}
                    </div>

                    {/* Multi-upload progress overlay */}
                    {isMultiUploading && (
                        <div className="multi-upload-overlay">
                            <div className="multi-upload-progress">
                                <Icon name="images" size="big" color="teal" />
                                <h3 style={{ margin: '16px 0 8px', color: '#1a1a1a' }}>Uploading Multiple Items</h3>
                                <p style={{ color: '#666', margin: '0 0 16px', fontSize: '14px' }}>
                                    {multiUploadProgress.current
                                        ? `Uploading: ${multiUploadProgress.current}`
                                        : 'Preparing...'
                                    }
                                </p>
                                <div style={{ width: '100%', maxWidth: '280px', height: '6px', background: '#e5e7eb', borderRadius: '3px', overflow: 'hidden' }}>
                                    <div style={{
                                        height: '100%',
                                        width: `${multiUploadProgress.total > 0 ? (multiUploadProgress.done / multiUploadProgress.total * 100) : 0}%`,
                                        background: '#00b5ad',
                                        borderRadius: '3px',
                                        transition: 'width 0.3s ease'
                                    }} />
                                </div>
                                <p style={{ color: '#999', fontSize: '13px', marginTop: '8px' }}>
                                    {multiUploadProgress.done} / {multiUploadProgress.total} items
                                </p>
                            </div>
                        </div>
                    )}
                </SusuModal.Content>
            </SusuModal>

            {/* Delete Confirmation Modal */}
            <ClothingDeleteConfirmModal
                isOpen={deleteConfirmOpen}
                onClose={handleCancelDelete}
                onConfirm={handleConfirmDelete}
                isDeleting={isOperationInProgress}
                deleteError={deleteError}
            />

            {/* VTO Deletion Warning Modal */}
            <DeletionWarningModal
                isOpen={showDeletionWarning}
                onClose={handleCancelDeletionWarning}
                onConfirm={handleConfirmCascadeDelete}
                itemType="clothing-item"
                vtoCount={vtoCount}
                isDeleting={isOperationInProgress}
                error={deletionError}
            />

            {/* Image Editor Modal */}
            <SusuModal
                open={isEditorOpen}
                onClose={() => !isSavingCrop && setIsEditorOpen(false)}
                size="large"
                className="image-editor-modal"
                closeIcon
            >
                <SusuModal.Header>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Icon name="crop" style={{ margin: 0 }} />
                        <span>Crop & Resize Image</span>
                    </div>
                </SusuModal.Header>
                <SusuModal.Content scrolling>
                    {editorImageUrl && (
                        <PersonImageEditor
                            imageUrl={editorImageUrl}
                            onSave={handleEditorSave}
                            onCancel={() => setIsEditorOpen(false)}
                            isSaving={isSavingCrop}
                        />
                    )}
                </SusuModal.Content>
            </SusuModal>

            {/* Background Removal Processing Modal */}
            <ProcessingModal
                isOpen={showBgRemovalProcessing}
                title="Background Removal"
                message="Removing background from your clothing image..."
                icon="image"
                creditBalance={creditBalance}
                creditsUsed={1}
                tip="Creating a clean cutout..."
            />
        </>
    );
};

export default ClothingModal;
